// In development, use the proxy (/api). In production, use the full Worker URL.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  correlationId: string;
}

class ApiError extends Error {
  code: string;
  correlationId: string;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    correlationId: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.correlationId = correlationId;
    this.details = details;
  }
}

async function request<T>(
  method: string,
  path: string,
  data?: unknown,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('mia-auth')
    ? JSON.parse(localStorage.getItem('mia-auth')!).state?.token
    : null;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    ...options,
  });

  const json = (await response.json()) as ApiResponse<T>;

  if (!response.ok || !json.success) {
    throw new ApiError(
      json.error?.message || 'Request failed',
      json.error?.code || 'UNKNOWN_ERROR',
      json.correlationId,
      json.error?.details
    );
  }

  return json;
}

export const api = {
  get: <T>(path: string, options?: RequestInit) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, data?: unknown, options?: RequestInit) =>
    request<T>('POST', path, data, options),
  put: <T>(path: string, data?: unknown, options?: RequestInit) =>
    request<T>('PUT', path, data, options),
  patch: <T>(path: string, data?: unknown, options?: RequestInit) =>
    request<T>('PATCH', path, data, options),
  delete: <T>(path: string, options?: RequestInit) => request<T>('DELETE', path, undefined, options),
};

export { ApiError };



